import React, { lazy, useEffect, useState } from 'react';
import { Route, Navigate, useParams } from 'react-router-dom';
import EPage from '@/enums/EPages';
import EBasicActions from '@/enums/EBasicActions';
import { PrivateRoute } from '@/hooks/useAuth';
import useAuth from '@/hooks/useAuth';
import canAccessEstablishment from '@/shared/helpers/canAccessEstablishment';

const EstablishmentsListPage = lazy(
  () => import('@/containers/Views/Establishments/EstablishmentsListPage'),
);
const EstablishmentEditPage = lazy(
  () => import('@/containers/Views/Establishments/EstablishmentEditPage'),
);
const EstablishmentNewPage = lazy(
  () => import('@/containers/Views/Establishments/EstablishmentNewPage'),
);
const EstablishmentStaffManager = lazy(
  () => import('@/containers/Views/Establishments/components/EstablishmentStaffManager'),
);
const EstablishmentWorkerManager = lazy(
  () => import('@/containers/Views/Establishments/components/EstablishmentWorkerManager'),
);
const EstablishmentProductManager = lazy(
  () => import('@/containers/Views/Establishments/components/EstablishmentProductManager'),
);
const EstablishmentMixManager = lazy(
  () => import('@/containers/Views/Establishments/components/EstablishmentMixManager'),
);
const OperativeLocationMixManager = lazy(
  () => import('@/containers/Views/Establishments/components/OperativeLocationMixManager'),
);
const EstablishmentDetails = lazy(
  () => import('@/containers/Views/Establishments/EstablishmentDetails'),
);
const EstablishmentDashboardPage = lazy(
  () => import('@/containers/Views/Establishments/EstablishmentDashboardPage'),
);
const ServiceTypeItemManager = lazy(
  () => import('@/containers/Views/Establishments/components/ServiceTypeItemManager'),
);
const OperativeLocationProductManager = lazy(
  () => import('@/containers/Views/Establishments/components/OperativeLocationProductManager'),
);

function EstablishmentGuard({ children }: { children: React.ReactElement }) {
  const { id, establishmentId } = useParams();
  const targetId = id ?? establishmentId;
  const auth = useAuth();
  const [isAllowed, setIsAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    canAccessEstablishment(auth?.staff?.id, auth?.staff?.email, targetId)
      .then(setIsAllowed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId, auth?.staff?.id]);

  if (isAllowed === null) return null;
  if (!isAllowed) return <Navigate to="/403" replace />;
  return children;
}

const EstablishmentsRouter = (
  <Route path="establishments">
    <Route
      index 
      element={(
        <PrivateRoute 
          pageName={EPage.Establishments} 
          permissions={EBasicActions.Find}
        >
          <EstablishmentsListPage />
        </PrivateRoute>
      )} 
    />
    <Route 
      path="new"
      element={(
        <PrivateRoute 
          pageName={EPage.Establishments} 
          permissions={EBasicActions.Create}
        >
          <EstablishmentNewPage />
        </PrivateRoute>
      )}
    />
    <Route
      path="dashboard"
      element={(
        <PrivateRoute
          pageName={EPage.Establishments}
          permissions={[EBasicActions.Find, EBasicActions.Get]}
        >
          <EstablishmentDashboardPage />
        </PrivateRoute>
      )}
    />
    <Route
      path="edit/:id"
      element={(
        <PrivateRoute
          pageName={EPage.Establishments}
          permissions={EBasicActions.Edit}
        >
          <EstablishmentGuard>
            <EstablishmentEditPage />
          </EstablishmentGuard>
        </PrivateRoute>
      )}
    />
    <Route
      path=":id/staff_manager"
      element={(
        <PrivateRoute
          pageName={EPage.EstablishmentStaff}
          permissions={[
            EBasicActions.Create,
            EBasicActions.Get,
            EBasicActions.Delete,
          ]}
        >
          <EstablishmentGuard>
            <EstablishmentStaffManager />
          </EstablishmentGuard>
        </PrivateRoute>
      )}
    />
    <Route
      path=":id/worker_manager"
      element={(
        <PrivateRoute
          pageName={EPage.EstablishmentStaff}
          permissions={[
            EBasicActions.Create,
            EBasicActions.Get,
            EBasicActions.Delete,
          ]}
        >
          <EstablishmentGuard>
            <EstablishmentWorkerManager />
          </EstablishmentGuard>
        </PrivateRoute>
      )}
    />
    <Route
      path=":id/product_manager"
      element={(
        <PrivateRoute
          pageName={EPage.EstablishmentProducts}
          permissions={[
            EBasicActions.Create,
            EBasicActions.Get,
            EBasicActions.Delete,
          ]}
        >
          <EstablishmentGuard>
            <EstablishmentProductManager />
          </EstablishmentGuard>
        </PrivateRoute>
      )}
    />
    <Route
      path=":id/mix_manager"
      element={(
        <PrivateRoute
          pageName={EPage.EstablishmentMixes}
          permissions={[
            EBasicActions.Create,
            EBasicActions.Get,
            EBasicActions.Delete,
          ]}
        >
          <EstablishmentGuard>
            <EstablishmentMixManager />
          </EstablishmentGuard>
        </PrivateRoute>
      )}
    />

    <Route
      path=":establishmentId/service_type/:serviceTypeId/product_manager"
      element={(
        <PrivateRoute
          pageName={EPage.EstablishmentProducts}
          permissions={EBasicActions.Edit}
        >
          <EstablishmentGuard>
            <ServiceTypeItemManager kind="product" />
          </EstablishmentGuard>
        </PrivateRoute>
      )}
    />
    <Route
      path=":establishmentId/service_type/:serviceTypeId/mix_manager"
      element={(
        <PrivateRoute
          pageName={EPage.EstablishmentMixes}
          permissions={EBasicActions.Edit}
        >
          <EstablishmentGuard>
            <ServiceTypeItemManager kind="mix" />
          </EstablishmentGuard>
        </PrivateRoute>
      )}
    />
    <Route
      path=":establishmentId/operative_location/:operativeLocationId/product_manager"
      element={(
        <PrivateRoute
          pageName={EPage.EstablishmentOperativeLocationProducts}
          permissions={EBasicActions.Get}
        >
          <EstablishmentGuard>
            <OperativeLocationProductManager />
          </EstablishmentGuard>
        </PrivateRoute>
      )}
    />
    <Route
      path=":establishmentId/operative_location/:operativeLocationId/mix_manager"
      element={(
        <PrivateRoute
          pageName={EPage.EstablishmentOperativeLocationMixes}
          permissions={EBasicActions.Get}
        >
          <EstablishmentGuard>
            <OperativeLocationMixManager />
          </EstablishmentGuard>
        </PrivateRoute>
      )}
    />
    <Route
      path=":id"
      element={(
        <PrivateRoute
          pageName={EPage.Establishments}
          permissions={EBasicActions.Get}
        >
          <EstablishmentGuard>
            <EstablishmentDetails />
          </EstablishmentGuard>
        </PrivateRoute>
      )}
    />
  </Route>
);

export default EstablishmentsRouter;
